class SwipeIndicator {
    constructor(options = {}) {
        this.options = {
            contentSelector: 'section[data-section-type="product"]',
            arrowLeftSelector: '.swipe-feedback-arrow--left',
            arrowRightSelector: '.swipe-feedback-arrow--right',
            minSwipeDistance: 50,
            minVisualDistance: 10,
            maxSwipeDist: 59,
            deadZone: 24,
            onSwipeLeft: null,
            onSwipeRight: null,
            ...options
        };

        this._initWhenReady();
    }

    /* ----------------------------------------------------------
     * Initialization
     * ---------------------------------------------------------- */

    _initWhenReady() {
        const el = document.querySelector(this.options.contentSelector);

        if (!el) {
            setTimeout(() => this._initWhenReady(), 200);
            return;
        }

        this.content = el;
        this.arrowLeft = document.querySelector(this.options.arrowLeftSelector);
        this.arrowRight = document.querySelector(this.options.arrowRightSelector);

        this._prepareArrow(this.arrowLeft);
        this._prepareArrow(this.arrowRight);

        this._bindEvents();
    }

    _prepareArrow(arrow) {
        if (!arrow) return;

        let blocked = arrow.querySelector('.swipe-feedback-arrow__blocked');
        if (!blocked) {
            blocked = document.createElement('div');
            blocked.className = 'swipe-feedback-arrow__blocked';
            blocked.textContent = '×';
            arrow.appendChild(blocked);
        }
        arrow._blocked = blocked;

        arrow._svg = arrow.querySelector('svg');
        arrow._strokes = arrow._svg ? arrow._svg.querySelectorAll('line, polyline') : null;
    }

    /* ----------------------------------------------------------
     * Arrow Rendering
     * ---------------------------------------------------------- */

    _updateArrow(direction, dx) {
        const arrow = direction === 'left' ? this.arrowLeft : this.arrowRight;
        if (!arrow) return 0;

        const {deadZone, maxSwipeDist, canSwipeLeft, canSwipeRight} = this.options;

        // progress value based on swipe distance
        let t = 0;
        if (Math.abs(dx) > deadZone) {
            t = Math.min(1, (Math.abs(dx) - deadZone) / maxSwipeDist);
        }

        const allowed = direction === 'left' ? canSwipeRight() : canSwipeLeft();

        if (!allowed && t > 0) {
            arrow._svg.style.opacity = 0;
            arrow._blocked.style.opacity = 1;
        } else {
            arrow._svg.style.opacity = 1;
            arrow._blocked.style.opacity = 0;
        }

        const intensity = 1.5;
        if (direction === 'left') {
            arrow.style.setProperty(
                '--swipe-x',
                `calc(${(-75 + 100 * t) * intensity}% - var(--swipe-edge-gap))`
            );
        } else {
            arrow.style.setProperty(
                '--swipe-x',
                `calc(${(75 - 100 * t) * intensity}% + var(--swipe-edge-gap))`
            );
        }

        arrow.style.setProperty('--swipe-progress', t);
        arrow.style.setProperty(
            '--swipe-color',
            allowed ? (t === 1 ? '#666' : '#999') : '#bbb'
        );

        return t;
    }

    _hideArrows() {
        [this.arrowLeft, this.arrowRight].forEach(arrow => {
            if (!arrow) return;
            arrow.style.setProperty('--swipe-progress', 0);
        });
    }

    /* ----------------------------------------------------------
     * Event Binding
     * ---------------------------------------------------------- */

    _bindEvents() {
        const {
            minVisualDistance,
            minSwipeDistance,
            deadZone,
            maxSwipeDist,
            onSwipeLeft,
            onSwipeRight
        } = this.options;

        let startX = 0;
        let startY = 0;
        let lastDx = 0;
        let isSwiping = false;
        let lockedDirection = null;

        const isModalActive = () =>
            !!document.querySelector('.modal.is-active');

        this.content.addEventListener('touchstart', e => {
            if (isModalActive() || e.touches.length !== 1) return;

            startX = e.touches[0].clientX;
            startY = e.touches[0].clientY;

            lastDx = 0;
            lockedDirection = null;
            isSwiping = true;

            this._hideArrows();
            this.content.style.transition = '';
            this.content.style.transform = '';
        });

        this.content.addEventListener('touchmove', e => {
            if (!isSwiping || e.touches.length !== 1) return;

            const dx = e.touches[0].clientX - startX;
            const dy = Math.abs(e.touches[0].clientY - startY);

            if (dy >= 40) return;

            lastDx = dx;

            const {deadZone, maxSwipeDist} = this.options;

            if (dx > 0) {
                this._updateArrow('left', dx);
                this._updateArrow('right', 0);
            } else if (dx < 0) {
                this._updateArrow('right', dx);
                this._updateArrow('left', 0);
            } else {
                this._hideArrows();
            }

            /* -------------------------
               ELASTIC IMAGE MOVEMENT
            -------------------------- */

            const maxSwipe = deadZone + maxSwipeDist;

            const limited = Math.max(
                -maxSwipe,
                Math.min(dx, maxSwipe)
            );

            // Elastic effect
            const elastic = limited * 0.7;

            this.content.style.transition = '';
            this.content.style.transform = `translateX(${elastic}px)`;

            e.preventDefault();
        }, {passive: false});

        this.content.addEventListener('touchend', () => {
            if (!isSwiping) return;

            isSwiping = false;

            const {canSwipeLeft, canSwipeRight, minSwipeDistance, deadZone} = this.options;

            const swipedRight = lastDx > minSwipeDistance;
            const swipedLeft = lastDx < -minSwipeDistance;

            //SUCCESSFUL SWIPE
            if (swipedLeft && canSwipeLeft()) {
                this.options.onSwipeLeft?.();
            } else if (swipedRight && canSwipeRight()) {
                this.options.onSwipeRight?.();
            } else if (Math.abs(lastDx) < deadZone) {
                // Pure tap or tiny movement — no shake needed.
                // Fully clear the transform so descendants with position:fixed
                // (e.g. the zoom modal) don't get their containing block
                // pinned to section.product.
                this._hideArrows();
                this.content.style.transition = '';
                this.content.style.transform = '';
            } else {
                const target = this.content;
                const shakeDistance = 10; // px
                const shakeDuration = 50; // ms per shake
                const shakes = [shakeDistance, -shakeDistance, shakeDistance / 2, -shakeDistance / 2, 0];

                // Reset arrows immediately
                this._hideArrows()

                // Apply shaking sequence
                shakes.forEach((x, i) => {
                    setTimeout(() => {
                        target.style.transition = `transform ${shakeDuration}ms ease`;
                        if (i === shakes.length - 1) {
                            target.style.transform = '';
                        } else {
                            target.style.transform = `translateX(${x}px)`;
                        }
                    }, i * shakeDuration);
                });
            }
        });
    }
}

window.SwipeIndicatorTd = SwipeIndicator;